// client/src/components/leagues/HomeCourtSelector.js
import React, { useEffect, useMemo, useState } from 'react'
import ApiCourts from '../../api/ApiCourts'
import toastr from 'toastr'
import DuplicateCourtModal from '../common/DuplicateCourtModal'

/**
 * HomeCourtSelector
 * - Autocomplete search against courts API
 * - Allows lightweight create when not found
 */
export default function HomeCourtSelector({
  value,
  onChange,
  city,
  state,
  zip,
  required,
}) {
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState([])
  const [creating, setCreating] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [duplicateCourts, setDuplicateCourts] = useState([])
  const [newCourt, setNewCourt] = useState({
    name: '',
    address_line1: '',
    city: city || '',
    state: state || '',
    zip: zip || '',
  })

  // Update newCourt when parent props change (e.g., city/state/zip)
  useEffect(() => {
    setNewCourt((prev) => ({
      ...prev,
      city: city || prev.city,
      state: state || prev.state,
      zip: zip || prev.zip,
    }))
  }, [city, state, zip])

  // Debounce search
  useEffect(() => {
    const h = setTimeout(async () => {
      const q = search.trim()
      if (q.length < 2) {
        setResults([])
        return
      }
      try {
        setLoading(true)
        const data = await ApiCourts.search({
          query: q,
          city,
          state,
          zip,
          limit: 8,
        })
        setResults(Array.isArray(data) ? data : [])
      } catch (e) {
        console.error('Court search failed', e)
      } finally {
        setLoading(false)
      }
    }, 250)
    return () => clearTimeout(h)
  }, [search, city, state, zip])

  const handlePick = (court) => {
    onChange?.(court)
  }

  const selectedLabel = useMemo(() => {
    if (!value) return ''
    const parts = [
      value.name,
      value.address_line1,
      value.city,
      value.state,
      value.zip,
    ].filter(Boolean)
    return parts.join(', ')
  }, [value])

  const canCreate = search.trim().length >= 3

  const handleCreate = async () => {
    if (!canCreate) return
    try {
      setCreating(true)
      const payload = {
        name: (newCourt.name || search).trim(),
        address_line1: newCourt.address_line1?.trim(),
        city: newCourt.city?.trim(),
        state: newCourt.state?.trim(),
        zip: newCourt.zip?.trim(),
      }
      if (
        !payload.name ||
        !payload.address_line1 ||
        !payload.city ||
        !payload.state ||
        !payload.zip
      ) {
        toastr.warning('Please complete all required court fields')
        return
      }
      const res = await ApiCourts.create(payload)
      const court = res.court
      if (court?.id) {
        onChange?.(court)
        toastr.success('Court created')
        setShowCreateForm(false)
      } else {
        toastr.error('Failed to create court')
      }
    } catch (e) {
      // Check if it's a duplicate error (409)
      if (e.response?.status === 409) {
        const matches =
          e.response?.data?.error?.details?.matches || e.response?.data?.matches
        if (matches) {
          setDuplicateCourts(matches)
          setShowDuplicateModal(true)
          return
        }
      }
      toastr.error(e?.message || 'Failed to create court')
    } finally {
      setCreating(false)
    }
  }

  const handleSelectExistingCourt = (court) => {
    onChange?.(court)
    toastr.success('Court selected')
    setShowDuplicateModal(false)
    setShowCreateForm(false)
    setDuplicateCourts([])
  }

  const handleCloseWithInfo = () => {
    // For MVP, we close the modal and show an informational message
    // In a future iteration, this could support force-creating the court
    setShowDuplicateModal(false)
    toastr.info('Please contact support if you believe this is a unique court')
  }

  const handleCloseDuplicateModal = () => {
    setShowDuplicateModal(false)
    setDuplicateCourts([])
  }

  return (
    <div>
      <label htmlFor="court-search-input" className="form-label">
        Home Court {required ? '*' : ''}
      </label>
      {value ? (
        <div className="d-flex align-items-center mb-2">
          <div className="flex-grow-1 small" aria-live="polite">
            {selectedLabel}
          </div>
          <button
            className="btn btn-sm btn-outline-secondary ms-2"
            onClick={() => onChange?.(null)}
            aria-label={`Change home court from ${value.name}`}
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <input
            id="court-search-input"
            type="text"
            className="form-control form-control-sm"
            placeholder="Type to search for courts by name or address..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search for courts"
            aria-describedby="court-search-help"
          />
          <div id="court-search-help" className="form-text">
            Start typing to search for existing courts, or create a new one if
            not found
          </div>
          {loading && (
            <div
              className="small text-muted mt-1"
              role="status"
              aria-live="polite"
            >
              Searching…
            </div>
          )}
          {results.length > 0 && (
            <ul
              className="list-group mt-2"
              role="listbox"
              aria-label="Court search results"
            >
              {results.map((c) => (
                <li
                  key={c.id}
                  className="list-group-item list-group-item-action"
                  role="option"
                  aria-selected="false"
                  tabIndex={0}
                  onClick={() => handlePick(c)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handlePick(c)
                    }
                  }}
                  aria-label={`${c.name}, ${[c.address_line1, c.city, c.state, c.zip].filter(Boolean).join(', ')}`}
                >
                  <div className="fw-semibold">{c.name}</div>
                  <div className="small text-muted">
                    {[c.address_line1, c.city, c.state, c.zip]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {results.length === 0 && canCreate && (
            <div className="mt-3">
              {!showCreateForm ? (
                <div className="text-center py-2 border rounded bg-light">
                  <p className="mb-1 text-muted small">
                    <i className="bi bi-search me-2"></i>
                    Court not found?
                  </p>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => {
                      setShowCreateForm(true)
                      setNewCourt((c) => ({ ...c, name: search.trim() }))
                    }}
                    aria-label={`Create new court named ${search.trim()}`}
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Add New Court
                  </button>
                </div>
              ) : (
                <div className="card card-body bg-light">
                  <h6 className="card-title mb-3">
                    <i className="bi bi-plus-circle me-2"></i>
                    Add New Court
                  </h6>
                  <div className="row g-2">
                    <div className="col-12">
                      <label
                        htmlFor="new-court-name"
                        className="form-label small fw-semibold"
                      >
                        Court Name{' '}
                        <span className="text-danger" aria-hidden="true">
                          *
                        </span>
                      </label>
                      <input
                        id="new-court-name"
                        name="name"
                        className="form-control form-control-sm"
                        placeholder="e.g., Oak Park Tennis Center"
                        value={newCourt.name}
                        onChange={(e) =>
                          setNewCourt({ ...newCourt, name: e.target.value })
                        }
                        aria-required="true"
                        autoComplete="off"
                      />
                    </div>
                    <div className="col-12">
                      <label
                        htmlFor="new-court-address"
                        className="form-label small fw-semibold"
                      >
                        Street Address{' '}
                        <span className="text-danger" aria-hidden="true">
                          *
                        </span>
                      </label>
                      <input
                        id="new-court-address"
                        name="address_line1"
                        className="form-control form-control-sm"
                        placeholder="e.g., 6350 Courtside Dr NW"
                        value={newCourt.address_line1}
                        onChange={(e) =>
                          setNewCourt({
                            ...newCourt,
                            address_line1: e.target.value,
                          })
                        }
                        aria-required="true"
                        autoComplete="street-address"
                      />
                    </div>
                    <div className="col-5">
                      <label
                        htmlFor="new-court-city"
                        className="form-label small fw-semibold"
                      >
                        City{' '}
                        <span className="text-danger" aria-hidden="true">
                          *
                        </span>
                      </label>
                      <input
                        id="new-court-city"
                        name="city"
                        className="form-control form-control-sm"
                        placeholder="e.g., Peachtree Corners"
                        value={newCourt.city}
                        onChange={(e) =>
                          setNewCourt({ ...newCourt, city: e.target.value })
                        }
                        aria-required="true"
                        autoComplete="address-level2"
                      />
                    </div>
                    <div className="col-3">
                      <label
                        htmlFor="new-court-state"
                        className="form-label small fw-semibold"
                      >
                        State{' '}
                        <span className="text-danger" aria-hidden="true">
                          *
                        </span>
                      </label>
                      <input
                        id="new-court-state"
                        name="state"
                        className="form-control form-control-sm"
                        placeholder="GA"
                        value={newCourt.state}
                        onChange={(e) =>
                          setNewCourt({ ...newCourt, state: e.target.value })
                        }
                        aria-required="true"
                        maxLength={2}
                        minLength={2}
                        autoComplete="address-level1"
                      />
                    </div>
                    <div className="col-4">
                      <label
                        htmlFor="new-court-zip"
                        className="form-label small fw-semibold"
                      >
                        ZIP{' '}
                        <span className="text-danger" aria-hidden="true">
                          *
                        </span>
                      </label>
                      <input
                        id="new-court-zip"
                        name="zip"
                        className="form-control form-control-sm"
                        placeholder="30092"
                        value={newCourt.zip}
                        onChange={(e) =>
                          setNewCourt({ ...newCourt, zip: e.target.value })
                        }
                        aria-required="true"
                        autoComplete="postal-code"
                      />
                    </div>
                  </div>
                  <div className="mt-3 d-flex gap-2">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={handleCreate}
                      disabled={creating}
                      aria-label="Create new court"
                    >
                      {creating ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden="true"
                          />
                          Creating…
                        </>
                      ) : (
                        'Create Court'
                      )}
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowCreateForm(false)}
                      disabled={creating}
                      aria-label="Cancel court creation"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <DuplicateCourtModal
        isOpen={showDuplicateModal}
        onClose={handleCloseDuplicateModal}
        onSelectExisting={handleSelectExistingCourt}
        onCreateAnyway={handleCloseWithInfo}
        duplicates={duplicateCourts}
        isProcessing={false}
      />
    </div>
  )
}
